import { Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from '@uirouter/react';

import { defaultPeriodValue } from '@/react/edge/edge-stacks/components/RetryDeployPeriod';
import { getDefaultStaggerConfig } from '@/react/edge/edge-stacks/components/StaggerFieldset.types';
import { notifySuccess } from '@/portainer/services/notifications';
import { getDefaultRelativePathModel } from '@/react/portainer/gitops/RelativePathFieldset/types';

import { useCreateWorkflow } from '../queries/useCreateWorkflow';
import { FormValues } from '../wizard/types';
import { WizardStep, useWizardContext } from '../wizard/WizardContext';
import { WizardHeader } from '../wizard/WizardHeader';
import { WizardFooter } from '../wizard/WizardFooter';
import { DeploymentType } from '../wizard/detectDeploymentType';
import { getWorkflowLink } from '../utils';

import { toCreateWorkflowPayload } from './toCreateWorkflowPayload';

type Props = {
  steps: WizardStep[];
};

export function CreateForm({ steps }: Props) {
  const { currentStep, isLastStep, goToNextStep } = useWizardContext();
  const router = useRouter();
  const createWorkflowMutation = useCreateWorkflow();

  const initialFormValues: FormValues = {
    name: '',
    sources: [],
    artifacts: [
      {
        name: '',
        deploymentType: DeploymentType.Kubernetes,
        files: [],
        targets: [],
        config: {
          envVars: [],
          prePullImage: false,
          retryDeploy: false,
          retryPeriod: defaultPeriodValue,
          parallelConfig: getDefaultStaggerConfig(),
          relativePath: getDefaultRelativePathModel(),
          useManifestNamespaces: false,
        },
      },
    ],
  };

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
      validationSchema={currentStep.validateStep}
    >
      <Form noValidate>
        <WizardHeader steps={steps} />
        <currentStep.Component />
        <WizardFooter />
      </Form>
    </Formik>
  );

  function handleSubmit(
    formValues: FormValues,
    { setTouched, setSubmitting }: FormikHelpers<FormValues>
  ) {
    if (!isLastStep) {
      goToNextStep();
      setTouched({});
      setSubmitting(false);
      return;
    }

    createWorkflowMutation.mutate(toCreateWorkflowPayload(formValues), {
      onSuccess: (workflow) => {
        notifySuccess('Success', 'Workflow created');
        if (workflow.id) {
          const { to, params } = getWorkflowLink({ id: workflow.id });
          router.stateService.go(to, params);
        } else {
          router.stateService.go('^');
        }
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  }
}
